import { useQuery } from "@tanstack/react-query";
import { useParams, useSearch, useNavigate } from "@tanstack/react-router";
import {
  Package2,
  Plus,
  Calendar,
  User,
  Building2,
  Clock,
  Users,
  DollarSign,
  TrendingUp,
  Mail,
  Edit,
  History,
} from "lucide-react";
import { useMemo, useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { SectionHeading } from "@/components/ui/section-heading";
import type {
  Project,
  ProjectMember,
  ProjectRevisionHistory,
} from "@/types/project.types";
import {
  OvertimeCreateDialog,
  OvertimeTable,
  ProjectAddMemberDialog,
  ProjectEditDialog,
  ProjectDeleteDialog,
  ProjectMembersTable,
  ProjectAccessManagement,
  StatusDropdown,
} from "./components";
import { OvertimeApprovalHistory } from "./components/OvertimeTable/OvertimeApprovalHistory";
import { apiService } from "@/lib/api-service";
import { useAuth } from "@/lib/auth-context";
import type { Employee } from "@employee-management/common";
import {
  ProjectStatus,
  UserRole,
  formatCurrency,
} from "@employee-management/common";
import clsx from "clsx";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";

const QuickStatsCard = ({
  title,
  value,
  icon,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
}) => {
  return (
    <div className="flex items-center space-x-3 p-4 border rounded-lg">
      <div className="w-12 h-12 rounded-lg border flex items-center justify-center">
        {icon}
      </div>
      <div>
        <p className="text-sm font-light text-primary-background">{title}</p>
        <p className="text-lg font-semibold dark:text-white text-black">
          {value}
        </p>
      </div>
    </div>
  );
};

export function ProjectDetailsView() {
  const { projectId } = useParams({ from: "/projects/$projectId" });
  const search = useSearch({ from: "/projects/$projectId" });
  const navigate = useNavigate();
  const { hasRole, user } = useAuth();
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // Determine if user can see financial information (manager or higher)
  const canSeeFinancialInfo = hasRole(UserRole.MANAGER);

  const {
    data: project,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => {
      const res = await apiService.get<Project>(`/projects/${projectId}`);
      return res;
    },
  });

  // Helper function to check if project is active
  const isProjectActive = useMemo(
    () => project?.status === ProjectStatus.ACTIVE,
    [project?.status]
  );

  const { data: employees } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => {
      const res =
        await apiService.get<(Employee & { id: string })[]>("/employees");
      return res;
    },
  });

  const { data: existingMembers } = useQuery({
    queryKey: ["project-members", projectId],
    queryFn: async () => {
      const res = await apiService.get<ProjectMember[]>(
        `/projects/${projectId}/members`
      );
      return res;
    },
  });

  const { data: projectRevisionHistory } = useQuery({
    queryKey: ["project-revision-history", projectId],
    queryFn: async () => {
      const res = await apiService.getPaginated<ProjectRevisionHistory>(
        `/projects/${projectId}/revision-history`
      );
      return res;
    },
    enabled: hasRole(UserRole.SUPERADMIN), // Only fetch for superadmins
  });

  // Check if user can manage access (manager/superadmin or project creator)
  const isProjectCreator = useMemo(
    () => project?.createdByRef?.id === user?.id,
    [project?.createdByRef?.id, user?.id]
  );

  const hasPermissionForAccessManagement = useMemo(
    () => hasRole(UserRole.MANAGER) || isProjectCreator,
    [hasRole, isProjectCreator]
  );

  // Check if user can edit project (manager/superadmin only, not supervisors)
  const canEditProject = useMemo(
    () => hasRole(UserRole.MANAGER) || hasRole(UserRole.SUPERADMIN),
    [hasRole]
  );

  // Handle tab changes and update URL
  const handleTabChange = useCallback(
    (value: string) => {
      setActiveTab(value);
      navigate({
        to: "/projects/$projectId",
        params: { projectId },
        search: { tab: value as any },
        replace: true,
      });
    },
    [navigate, projectId]
  );

  // Validate and set active tab based on search params and permissions
  useEffect(() => {
    const requestedTab = search.tab || "overview";

    if (requestedTab !== activeTab) {
      // Validate access tab permissions
      if (requestedTab === "access" && !hasPermissionForAccessManagement) {
        handleTabChange("overview");
      }
      // Validate history tab permissions
      else if (requestedTab === "history" && !hasRole(UserRole.SUPERADMIN)) {
        handleTabChange("overview");
      } else {
        setActiveTab(requestedTab);
      }
    }
  }, [
    search.tab,
    hasPermissionForAccessManagement,
    hasRole,
    activeTab,
    handleTabChange,
  ]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">
            Loading project details...
          </span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
            <Package2 className="w-8 h-8 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-red-600">
              Failed to load project
            </h3>
            <p className="text-muted-foreground">
              Please try refreshing the page
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 flex items-center justify-center">
            <Package2 className="w-8 h-8 text-gray-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Project not found</h3>
            <p className="text-muted-foreground">
              The project you're looking for doesn't exist
            </p>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-8 w-full">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="relative">
          <div className="flex items-start justify-between">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-2.5 dark:bg-primary/10 bg-primary/80 rounded-lg dark:text-primary text-primary-background mb-3">
                <Package2 className="w-4 h-4" />
                <p className="text-xs font-medium">Project</p>
              </div>

              <div className="flex items-center gap-4">
                <h1 className="text-4xl font-bold tracking-tight text-primary-background">
                  {project.name}
                </h1>

                <StatusDropdown
                  projectId={projectId}
                  currentStatus={project.status as ProjectStatus}
                  canEditProject={canEditProject}
                />
              </div>

              {project.description && (
                <p className="text-lg text-gray-600 max-w-2xl mt-1">
                  {project.description}
                </p>
              )}
            </div>
          </div>

          <div className="bg-primary-foreground rounded-lg py-3 px-1 mt-4 !hidden ">
            <div
              className={clsx(
                "flex items-center",
                "divide-x *:px-1.5 -mx-1.5 space-x-1.5 divide-primary/50",
                "[&>*]:rounded-none"
              )}
            >
              <div>
                <Button variant="link" size="sm" color="primary">
                  <Plus className="w-4 h-4" />
                  Add Note
                </Button>
              </div>
              <div>
                <Button variant="link" size="sm">
                  <Edit className="w-4 h-4" />
                  Edit
                </Button>
              </div>
              <div>
                <Button variant="link" size="sm">
                  <Clock className="w-4 h-4" />
                  Add Overtime Entry
                </Button>
              </div>
              <div>
                <Button variant="link" size="sm">
                  <Users className="w-4 h-4" />
                  Add Member
                </Button>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            <QuickStatsCard
              title="Client"
              value={project.clientName}
              icon={<Building2 className="w-5 h-5 text-blue-600" />}
            />

            <QuickStatsCard
              title="Start Date"
              value={formatDate(project.startDate)}
              icon={<Calendar className="w-5 h-5 text-green-600" />}
            />

            <QuickStatsCard
              title="Team Members"
              value={(existingMembers?.length || 0).toString()}
              icon={<Users className="w-5 h-5 text-purple-600" />}
            />

            <QuickStatsCard
              title="Duration"
              value="Ongoing"
              icon={<Clock className="w-5 h-5 text-orange-600" />}
            />
          </div>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="space-y-6 w-full"
      >
        <ScrollArea>
          <TabsList
            className={clsx(
              "flex justify-center gap-2 py-2.5 px-2 rounded-lg mb-4 h-auto bg-muted/50",
              "lg:grid",
              hasRole(UserRole.SUPERADMIN)
                ? "grid-cols-5"
                : hasPermissionForAccessManagement
                  ? "grid-cols-4"
                  : "grid-cols-3"
            )}
          >
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="billing">Overtime</TabsTrigger>
            {hasPermissionForAccessManagement && (
              <TabsTrigger value="access">Access</TabsTrigger>
            )}
            {hasRole(UserRole.SUPERADMIN) && (
              <TabsTrigger value="history">History</TabsTrigger>
            )}
          </TabsList>

          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        <TabsContent value="overview" className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Project Details Card */}
              <Card className="border bg-muted/20">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center space-x-2 text-xl">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    <span>Project Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Building2 className="w-4 h-4" />
                        <span>Client</span>
                      </div>
                      <p className="text-lg font-medium">
                        {project.clientName}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span>Created</span>
                      </div>
                      <p className="text-lg font-medium">
                        {formatDate(project.createdAt)}
                      </p>
                    </div>
                  </div>
                  {project.internalProjectId && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <Package2 className="w-4 h-4" />
                          <span>Internal Project ID</span>
                        </div>
                        <p className="text-lg font-medium">
                          {project.internalProjectId}
                        </p>
                      </div>
                    </div>
                  )}

                  <Separator />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span>Start Date</span>
                      </div>
                      <p className="text-lg font-medium">
                        {formatDate(project.startDate)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Overtime & Payment Status Card */}
              <Card className="border bg-muted/20">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center space-x-2 text-xl">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    <span>Overtime & Payment Status</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {project.metadata ? (
                    <div className="space-y-6">
                      {/* Approved Overtime Section */}
                      <div className="space-y-3">
                        <h4 className="font-semibold text-sm text-secondary-foreground border-b pb-2">
                          Approved Overtime
                        </h4>
                        <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
                          <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                            <span className="text-sm font-medium text-secondary-foreground">
                              Total Entries
                            </span>
                            <span className="text-sm font-medium">
                              {project.metadata.totalApprovedOvertimeEntries}
                            </span>
                          </div>
                          <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                            <span className="text-sm font-medium text-secondary-foreground">
                              Total Hours
                            </span>
                            <span className="text-sm font-medium">
                              {project.metadata.totalApprovedHours.toFixed(1)}{" "}
                              hrs
                            </span>
                          </div>
                          {canSeeFinancialInfo && (
                            <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                              <span className="text-sm font-medium text-secondary-foreground">
                                Total Amount
                              </span>
                              <span className="text-sm font-medium">
                                {formatCurrency(
                                  project.metadata.totalApprovedAmount
                                )}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                            <span className="text-sm font-medium text-secondary-foreground">
                              Pending Payment
                            </span>
                            <span className="text-sm font-medium">
                              {project.metadata.pendingApprovedOvertimeEntries}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Payment Status Section (Managers and above only) */}
                      {canSeeFinancialInfo && (
                        <div className="space-y-3">
                          <h4 className="font-semibold text-sm text-secondary-foreground border-b pb-2">
                            Payment Status
                          </h4>
                          <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
                            <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                              <span className="text-sm font-medium text-secondary-foreground">
                                Paid Amount
                              </span>
                              <span className="text-sm font-medium text-green-600">
                                {formatCurrency(
                                  project.metadata.paymentApprovedAmount
                                )}
                              </span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                              <span className="text-sm font-medium text-secondary-foreground">
                                Pending Amount
                              </span>
                              <span className="text-sm font-medium text-orange-600">
                                {formatCurrency(
                                  project.metadata.paymentPendingAmount
                                )}
                              </span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                              <span className="text-sm font-medium text-secondary-foreground">
                                Paid Entries
                              </span>
                              <span className="text-sm font-medium text-green-600">
                                {project.metadata.paymentApprovedCount}
                              </span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                              <span className="text-sm font-medium text-secondary-foreground">
                                Pending Entries
                              </span>
                              <span className="text-sm font-medium text-orange-600">
                                {project.metadata.paymentPendingCount}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Pending Overtime Section */}
                      <div className="space-y-3">
                        <h4 className="font-semibold text-sm text-secondary-foreground border-b pb-2">
                          Pending Approval
                        </h4>
                        <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
                          <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                            <span className="text-sm font-medium text-secondary-foreground">
                              Total Hours
                            </span>
                            <span className="text-sm font-medium">
                              {project.metadata.totalPendingHours.toFixed(1)}{" "}
                              hrs
                            </span>
                          </div>
                          {canSeeFinancialInfo && (
                            <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                              <span className="text-sm font-medium text-secondary-foreground">
                                Total Amount
                              </span>
                              <span className="text-sm font-medium">
                                {formatCurrency(
                                  project.metadata.totalPendingAmount
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Last Updated */}
                      {project.metadata.paymentUpdatedAt && (
                        <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                          <span className="text-sm font-medium text-secondary-foreground">
                            Last Updated
                          </span>
                          <span className="text-sm font-medium">
                            {formatDate(
                              project.metadata.paymentUpdatedAt.toString()
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center p-3 bg-muted/20 rounded-lg">
                      <span className="text-sm font-medium text-secondary-foreground">
                        No overtime data available
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Team Members Card */}
              <Card className="border bg-muted/20">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center space-x-2 text-xl">
                    <Users className="w-5 h-5 text-green-600" />
                    <span>Key Team Members</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Handler Member */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Badge
                        variant="outline"
                        className="bg-blue-50 text-blue-700 border-blue-200"
                      >
                        Handler
                      </Badge>
                    </div>
                    <div className="p-4 bg-gradient-to-r rounded-xl border bg-muted/20">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 space-y-1">
                          <h4 className="font-semibold text-secondary-foreground">
                            {project.handlerMemberRef.name}
                          </h4>
                          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                            <div className="flex items-center space-x-1">
                              <Mail className="w-4 h-4" />
                              <span>{project.handlerMemberRef.email}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sales Team Member */}
                  {project.salesTeamMemberRef && (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Badge
                            variant="outline"
                            className="bg-green-50 text-green-700 border-green-200"
                          >
                            Sales
                          </Badge>
                        </div>
                        <div className="p-4 bg-gradient-to-r rounded-xl border bg-muted/20">
                          <div className="flex items-start space-x-4">
                            <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center">
                              <User className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1 space-y-1">
                              <h4 className="font-semibold text-secondary-foreground">
                                {project.salesTeamMemberRef.name}
                              </h4>
                              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                                <div className="flex items-center space-x-1">
                                  <Mail className="w-4 h-4" />
                                  <span>
                                    {project.salesTeamMemberRef.email}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="border bg-muted/20">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center space-x-2 text-xl">
                    <Clock className="w-5 h-5 text-purple-600" />
                    <span>Overtime History</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {project.lastOvertimeHistory ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 dark:bg-gray-100/10 bg-gray-900/10 rounded-lg">
                        <span className="text-sm font-medium text-secondary-foreground">
                          Person
                        </span>
                        <span className="text-sm font-medium">
                          {project.lastOvertimeHistory?.changedByRef.name}
                        </span>
                      </div>

                      <div className="flex items-center justify-between p-3 dark:bg-gray-100/10 bg-gray-900/10 rounded-lg">
                        <span className="text-sm font-medium text-secondary-foreground">
                          Date
                        </span>
                        <span className="text-sm font-medium">
                          {formatDate(
                            project.lastOvertimeHistory?.changedAt || ""
                          )}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center p-3 bg-muted/20 rounded-lg">
                      <span className="text-sm font-medium text-secondary-foreground">
                        No overtime history
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Project Stats */}
              <Card className="border bg-muted/20">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center space-x-2 text-xl">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                    <span>Project Stats</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                      <span className="text-sm font-medium text-secondary-background">
                        Status
                      </span>
                      <Badge
                        variant={
                          project.status === ProjectStatus.ACTIVE
                            ? "default"
                            : "secondary"
                        }
                        className={
                          project.status === ProjectStatus.ACTIVE
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }
                      >
                        {project.status === ProjectStatus.ACTIVE
                          ? "Active"
                          : "Inactive"}
                      </Badge>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                      <span className="text-sm font-medium text-secondary-foreground">
                        Created By
                      </span>
                      <span className="text-sm font-medium">
                        {project.createdByRef?.name}
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                      <span className="text-sm font-medium text-secondary-foreground">
                        Last Updated
                      </span>
                      <span className="text-sm font-medium">
                        {formatDate(project.updatedAt)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {canEditProject && (
                <div className="flex flex-col justify-center items-stretch w-full space-y-3">
                  <ProjectEditDialog project={project} />
                  <ProjectDeleteDialog project={project} />
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="members" className="space-y-8">
          {!isProjectActive && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Project is not active
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      This project is currently {project.status.toLowerCase()}.
                      You cannot add new members or overtime entries until the
                      project is set to active.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <SectionHeading
            title={
              <div className="flex items-center space-x-2">
                <Users className="w-8 h-8 text-blue-600" />
                <span>Project Members</span>
              </div>
            }
            subtitle="Team members working on this project (separate from handler and sales roles)."
            actions={
              <Button
                onClick={() => setAddMemberOpen(true)}
                disabled={!isProjectActive}
                title={
                  !isProjectActive
                    ? "Cannot add members to non-active projects"
                    : "Add member to project"
                }
              >
                <Plus className="h-5 w-5" />
                Add Member
              </Button>
            }
            size="lg"
          />

          <ProjectMembersTable
            projectId={projectId}
            projectStatus={project.status}
          />

          <ProjectAddMemberDialog
            open={addMemberOpen}
            onOpenChange={setAddMemberOpen}
            projectId={projectId}
            employees={employees || []}
            existingMembers={existingMembers || []}
          />
        </TabsContent>

        <TabsContent value="billing" className="space-y-8">
          {!isProjectActive && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Project is not active
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      This project is currently {project.status.toLowerCase()}.
                      You cannot add new overtime entries until the project is
                      set to active.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <SectionHeading
            title={
              <div className="flex items-center space-x-2">
                <DollarSign className="w-8 h-8 text-green-600" />
                <span>Overtime</span>
              </div>
            }
            subtitle="Track and manage overtime entries for this project."
            actions={
              <OvertimeCreateDialog
                projectId={projectId}
                projectMembers={[...(existingMembers || [])].map((member) => ({
                  id: member.id,
                  name: member.name,
                  email: member.email,
                  department: member.department,
                  hourlyRate: member.hourlyRate || 25,
                }))}
                disabled={!isProjectActive}
              />
            }
            size="lg"
          />

          <OvertimeTable
            projectId={projectId}
            isActive={isProjectActive}
            projectMembers={[...(existingMembers || [])].map((member) => ({
              id: member.id,
              name: member.name,
              email: member.email,
              department: member.department,
              hourlyRate: member.hourlyRate || 25,
            }))}
          />
        </TabsContent>

        {hasPermissionForAccessManagement && (
          <TabsContent value="access" className="space-y-8">
            <ProjectAccessManagement
              projectId={projectId}
              isProjectCreator={isProjectCreator}
            />
          </TabsContent>
        )}

        {hasRole(UserRole.SUPERADMIN) && (
          <TabsContent value="history" className="space-y-8">
            <SectionHeading
              title={
                <div className="flex items-center space-x-2">
                  <History className="w-8 h-8 text-purple-600" />
                  <span>Project Revision History</span>
                </div>
              }
              subtitle="Complete audit trail of all changes made to this project."
              size="lg"
            />

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <OvertimeApprovalHistory
                  history={projectRevisionHistory?.data || []}
                />
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
